





































































































































































import Vue from 'vue';
import store from '@/store';
import { OrgStructure, OrgUser, OrgPeerGroup } from '@/models/orgStructure';
import * as Utils from '@/utils';
import { WebRequestStatus, BaseOrganization } from '@/models';
import { Packet } from '@/models/packet';
import { GoogleClassroomCourse } from '@/api/auth/google';

import CogsDefinition from '@/components/widgets/CogsDefinition.vue';
import OrganizationLoadGuard from '@/components/OrganizationLoadGuard.vue';
import OrganizationPacketList from '@/components/OrganizationPacketList.vue';
import OrganizationPeerGroupClassroomImporter from '@/components/OrganizationPeerGroupClassroomImporter.vue';
import OrganizationPeerGroupCreator from '@/components/OrganizationPeerGroupCreator.vue';
import OrganizationUserList from '@/components/OrganizationUserList.vue';
import PeerGroupList from '@/components/PeerGroupList.vue';
import PersistableItem from '@/components/PersistableItem.vue';
import UnrulrFilter from '@/components/Filter.vue';

enum View {
  GROUPS,
  MEMBERS,
  GOALS
}

export default Vue.extend({
  components: {
    CogsDefinition,
    OrganizationLoadGuard,
    OrganizationPacketList,
    OrganizationPeerGroupCreator,
    OrganizationPeerGroupClassroomImporter,
    OrganizationUserList,
    PeerGroupList,
    PersistableItem,
    UnrulrFilter
  },

  props: {
    organizationId: Number
  },

  data() {
    return {
      filter: '',
      groupCount: 0,
      memberCount: 0,
      goalCount: 0,
      view: View.GROUPS,
      tempCreateId: Utils.idGenerator.next()
    };
  },

  computed: {
    org(): OrgStructure {
      return store.state.org.org;
    },
    organization(): BaseOrganization {
      return store.state.org.basic;
    },
    users(): [OrgUser, WebRequestStatus][] {
      return this.org.users
        .slice()
        .sort(Utils.sortUsers)
        .map(u => [u, store.getters.org.status.user(u.userId)]);
    },
    packets(): Packet[] {
      return this.org.packets.map(p => p.packet).sort(Utils.sortNamed);
    },
    peerGroups(): OrgPeerGroup[] {
      return this.org.peerGroups.slice().sort(Utils.sortNamed);
    },
    adminAccess(): boolean {
      return store.state.org.adminAccess;
    },
    organizerAccess(): boolean {
      return store.state.org.organizerAccess;
    },
    createEnabled(): boolean {
      return store.state.org.organizerAccess || store.state.org.adminAccess;
    },
    isGroupsView(): boolean {
      return this.view === View.GROUPS;
    },
    isMembersView(): boolean {
      return this.view === View.MEMBERS;
    },
    isGoalsView(): boolean {
      return this.view === View.GOALS;
    },
    createStatus(): WebRequestStatus {
      return store.state.org.activeRequests.create.getStatus(this.tempCreateId);
    },

    placeholderText(): string {
      switch (this.view) {
        case View.GROUPS:
          return 'Type to find or create a group';
        case View.MEMBERS:
          return 'Type to find or add a member';
        case View.GOALS:
          return 'Type to find cogs';
      }

      return '';
    },

    hintText(): string | undefined {
      switch (this.view) {
        case View.GROUPS:
          return undefined;
        case View.MEMBERS:
          return '* Paste in a list of emails to add multiple users at once';
        case View.GOALS:
          return undefined;
      }

      return undefined;
    }
  },

  methods: {
    updateFilter(value: string) {
      this.filter = value;
    },

    createOrgUser(email: string) {
      store.dispatch.org.addNewUserToOrg({ email });
    },

    async createOrgUsers(emails: string[]) {
      for (let i = 0; i < emails.length; i++) {
        await store.dispatch.org.addNewUserToOrg({
          email: emails[i],
          delay: 0
        });
      }
    },

    async createOrgGroup(group: {
      name: string;
      section: string;
      description: string;
    }) {
      const newGroupId = await store.dispatch.org.createPeerGroup({
        tempCreateId: this.tempCreateId,
        ...group
      });

      this.finishUpGroupCreation(newGroupId);
    },

    async importClassroomCourse({ course }: { course: GoogleClassroomCourse }) {
      const newGroupId = await store.dispatch.org.importClassroomCourse({
        tempCreateId: this.tempCreateId,
        course
      });

      this.finishUpGroupCreation(newGroupId);
    },

    finishUpGroupCreation(newGroupId: number) {
      if (newGroupId == -1) {
        return;
      }

      const orgId = this.$route.params['organizationId'] as string;

      this.$router.push({
        name: 'OrganizationPeerGroup',
        params: {
          organizationId: orgId,
          peerGroupId: `${newGroupId}`
        }
      });
    },

    goToLtiRegistration() {
      this.$router.push({
        name: 'LtiRegistration',
        params: { organizationId: `${this.organizationId}` }
      });
    },

    buildGoals() {
      this.$router.push({
        name: 'OrganizationPacketCreator'
      });
    },

    toggleGroups() {
      this.setRoute(View.GROUPS);
    },
    toggleMembers() {
      this.setRoute(View.MEMBERS);
    },
    toggleGoals() {
      this.setRoute(View.GOALS);
    },

    setView(view: View) {
      this.view = view;
    },

    setRoute(view: View) {
      this.$router.push({
        name: 'Organization',
        params: { organizationId: `${this.organizationId}` },
        query: {
          view: `${view}`
        }
      });
    },

    handleGroupFilter({ count }: { count: number }) {
      this.groupCount = count;
    },
    handleMemberFilter({ count }: { count: number }) {
      this.memberCount = count;
    },
    handleGoalFilter({ count }: { count: number }) {
      this.goalCount = count;
    },

    parseViewFromRoute() {
      const routeView = +(this.$route.query.view || 0);

      if (routeView != this.view) {
        this.setView(routeView);
      }
    }
  },

  watch: {
    $route() {
      this.parseViewFromRoute();
    }
  },

  created() {
    this.parseViewFromRoute();
  }
});
